import { PolicyWorksheetMode } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/server/src/API/Policy/PolicyWorksheets.Controller.ts

/** Wire DTOs — mirror apps/server/src/API/Policy/PolicyWorksheets.Service.ts. */

export type WorksheetItemVisibilityDTO = "visible" | "staff_only" | "swivl_staff_controlled";

/** A Swivl-staff per-scope visibility choice for one swivl_staff_controlled item. */
export type PolicyItemVisibilityChoiceDTO = "visible" | "staff_only";

export interface WorksheetItemViewDTO {
  itemKey: string;
  kind:
    | "additive_checkbox"
    | "boolean_checkbox"
    | "radio"
    | "dropdown"
    | "parameterized_option"
    | "multi_checkbox"
    | "text_value"
    | "number_value"
    | "dynamic_reference"
    | "static_text"
    | "hint";
  label: string;
  /** Swivl-staff only: the authored visibility. Operators never receive hidden items at all. */
  visibility?: WorksheetItemVisibilityDTO;
  hint?: string;
  /** text_value / number_value: placeholder shown in the empty input. */
  placeholder?: string;
  /** text_value / number_value sentence template; sent for operator preview. */
  sentenceTemplate?: string;
  /** Present for `hint` items only — operator guidance. */
  body?: string;
  /** hint: optional cross-reference to another worksheet (target policyKey + clickable label). */
  linkPolicyKey?: string;
  linkLabel?: string;
  /** hint: link to this URLKey's field on the URLs page, showing its current value. */
  linksToUrlKey?: string;
  options?: { optionKey: string; label: string }[];
  defaultValue?: string;
  /** Conditional display gate — show this item only while the target's effective value matches. */
  showIf?: { itemKey: string; equals: string };
}

export type WorksheetAnswerInheritanceDTO = "none" | "group" | "org" | "default";

export interface WorksheetAnswerViewDTO {
  value: string;
  inheritance: WorksheetAnswerInheritanceDTO;
}

export interface PolicyWorksheetViewDTO {
  policyKey: string;
  title: string;
  /** One-line subtitle shown under the title (display only). */
  description?: string;
  groupLabel?: string;
  sortOrder: number;
  items: WorksheetItemViewDTO[];
  /** Example customer questions this policy should answer — shown to operators to orient the form. */
  probeQuestions?: string[];
  answers: { [itemKey: string]: WorksheetAnswerViewDTO };
  revision: number;
  /** Swivl-staff only: this scope's own row mode ("generate" when no row exists). Absent for operators. */
  mode?: PolicyWorksheetMode;
  /** Swivl-staff only: this scope's own custom text (custom mode). Absent for operators. */
  customText?: string;
  /** Swivl-staff only: an ANCESTOR scope's custom text applies here (D6 nearest-declaration wins). */
  inheritedCustom?: boolean;
  /** Swivl-staff only: effective visibility per swivl_staff_controlled item ("default" inheritance = undeclared → staff_only). */
  itemVisibility?: { [itemKey: string]: { value: PolicyItemVisibilityChoiceDTO; inheritance: WorksheetAnswerInheritanceDTO } };
}

export interface PolicyWorksheetsResponseDTO {
  worksheets: PolicyWorksheetViewDTO[];
}

export interface SaveWorksheetAnswersRequestDTO {
  groupId?: number;
  locationId?: number;
  answers: Record<string, string>;
  revision: number;
}

export interface SetWorksheetModeRequestDTO {
  groupId?: number;
  locationId?: number;
  mode: PolicyWorksheetMode;
  customText?: string;
}

export interface SetItemVisibilityRequestDTO {
  groupId?: number;
  locationId?: number;
  /** Full replacement of the choices set at this scope (omit a key to inherit again). */
  itemVisibility: Record<string, PolicyItemVisibilityChoiceDTO>;
}

export interface WorksheetSaveResponseDTO {
  ok: boolean;
  error?: string;
  revision?: number;
}

export interface GeneratedPolicyViewDTO {
  policyKey: string;
  text: string;
}

export class PolicyWorksheetsAPI {
  static async getWorksheets(orgId: number, groupId?: number, locationId?: number): Promise<PolicyWorksheetsResponseDTO> {
    const query: Record<string, number> = {};
    if (groupId != null) query["groupId"] = groupId;
    if (locationId != null) query["locationId"] = locationId;
    return APIService.Server.get<PolicyWorksheetsResponseDTO>(`/policy-worksheets/org/${orgId}`, query);
  }

  static async saveAnswers(orgId: number, policyKey: string, req: SaveWorksheetAnswersRequestDTO): Promise<WorksheetSaveResponseDTO> {
    return APIService.Server.put<WorksheetSaveResponseDTO>(`/policy-worksheets/org/${orgId}/policy/${policyKey}/answers`, req);
  }

  static async setMode(orgId: number, policyKey: string, req: SetWorksheetModeRequestDTO): Promise<WorksheetSaveResponseDTO> {
    return APIService.Server.put<WorksheetSaveResponseDTO>(`/policy-worksheets/org/${orgId}/policy/${policyKey}/mode`, req);
  }

  /** Swivl-staff: set per-scope visibility for swivl_staff_controlled items. */
  static async setItemVisibility(orgId: number, policyKey: string, req: SetItemVisibilityRequestDTO): Promise<WorksheetSaveResponseDTO> {
    return APIService.Server.put<WorksheetSaveResponseDTO>(`/policy-worksheets/org/${orgId}/policy/${policyKey}/item-visibility`, req);
  }

  /** Bot-facing (INTERNAL auth): derived policy texts, references resolved. */
  static async getPolicyTexts(orgId: number, policyKeys: string[], locationId?: number): Promise<Record<string, string>> {
    if (!policyKeys || policyKeys.length === 0) {
      return {};
    }
    const payload: Record<string, unknown> = { policyKeys };
    if (locationId != null) payload["locationId"] = locationId;
    return APIService.Server.get<Record<string, string>>(`/policy-texts/org/${orgId}`, payload);
  }

  /** Bot-facing (INTERNAL auth): raw per-item answers for one policy, merged org->group->location. */

  static async getPolicyAnswers(orgId: number, policyKey: string, locationId?: number): Promise<Record<string, string>> {
    const payload: Record<string, unknown> = { policyKey };
    if (locationId != null) payload["locationId"] = locationId;
    return APIService.Server.get<Record<string, string>>(`/policy-texts/org/${orgId}/answers`, payload);
  }

  /** Staff live preview: derived text with unsaved draft answers overlaid. */
  static async previewGeneratedText(
    orgId: number,
    policyKey: string,
    req: SaveWorksheetAnswersRequestDTO
  ): Promise<{ ok: boolean; text?: string; error?: string }> {
    return APIService.Server.post(`/policy-worksheets/org/${orgId}/policy/${policyKey}/preview`, req);
  }

  static async getGeneratedTexts(orgId: number, locationId?: number): Promise<GeneratedPolicyViewDTO[]> {
    return APIService.Server.get<GeneratedPolicyViewDTO[]>(
      `/policy-worksheets/org/${orgId}/generated`,
      locationId != null ? { locationId } : undefined
    );
  }
}
